import { FC } from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'
import { ExpensesExpensableFilter } from 'generated/resources/datatable.graphql'
import {
  ExpensesEntitiesExpensableStatusEnum,
  ReimbursementsPendingApprovalWidgetDocument,
  ReimbursementsPendingApprovalWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number, currency: string) => string
  forcedCurrency: string
}

interface QueryArgsType {
  from?: string
  to?: string
  employeeIds?: string[]
  groupIds?: string[]
  statuses: ExpensesEntitiesExpensableStatusEnum[]
}

const prepareData = (
  data: ReimbursementsPendingApprovalWidgetQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const hasMultipleCurrencies =
    (data?.expenses?.expensablesConnection?.aggregate?.terms?.reimbursableCurrency?.values
      ?.length || 0) > 1
  const reimbursableCurrency =
    data?.expenses?.expensablesConnection?.aggregate?.terms?.reimbursableCurrency?.values?.[0] ||
    prepareDataArgs.forcedCurrency
  const totalAmount =
    data?.expenses?.expensablesConnection?.aggregate?.terms?.reimbursableCurrency?.sum
      ?.reimbursableAmount?.[0] || 0
  const totalCount =
    data?.expenses?.expensablesConnection?.aggregate?.terms?.reimbursableCurrency?.count || 0

  return {
    countersChartDatas: [
      {
        value: totalAmount,
        formattedValue: prepareDataArgs.amountFormatter(totalAmount, reimbursableCurrency),
        label: '',
      },
    ],
    hasMultipleCurrencies,
    totalCount,
  }
}

export const buildReimbursementsPendingApprovalWidget = ({
  widgetTitle,
  queryArgs,
  prepareDataArgs,
  filters,
}: {
  widgetTitle?: string
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
  filters: ExpensesExpensableFilter
}) => {
  const ReimbursementsPendingApprovalWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas, hasMultipleCurrencies, totalCount } = data

    if (hasMultipleCurrencies) {
      return (
        <Alert color="note">{i18n.t('expenses_v2.list.insights.empty_case.description')}</Alert>
      )
    }

    return (
      <FinanceWidget
        id="reimbursements-pending-approval-widget"
        title={widgetTitle}
        type={SupportedCharts.COUNTERS}
        showChart={false}
        aspect="wide"
        countersChartDatas={countersChartDatas.map((chartData) => ({
          ...chartData,
        }))}
      >
        <Box>
          <Text size="200">
            {i18n.t('expenses_v2.list.insights.labels.expenses_count', {
              count: totalCount,
            })}
          </Text>
        </Box>
      </FinanceWidget>
    )
  }

  const filteredFilters = removeNullBetween(filters)

  return buildWidget({
    id: 'reimbursementsPendingApprovalWidget',
    component: ReimbursementsPendingApprovalWidget,
    document: ReimbursementsPendingApprovalWidgetDocument,
    prepareData: (data: ReimbursementsPendingApprovalWidgetQuery) =>
      prepareData(data, prepareDataArgs),
    queryArgs: {
      ...queryArgs,
      filter: {
        ...filteredFilters,
      },
    },
  })
}
